import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable, of, from } from 'rxjs';
import { ApiService } from '../../api/api.service';
import { ElementService } from '../element/element.service';
import { ModuleService } from '../module/module.service';
import { SubmoduleService } from '../submodule/submodule.service';
import { ElementLogs, ElementChange, ElementCommentSummary, ElementComment } from './logs.interface';
import { Project } from '../project/project.interface';
import { ProjectElement } from '../element/project-element.interface';
import { UserService } from '../user/user.service';
import { ConstantsService } from '../../utilities/constants/constants.service';
import { TranslateService } from '../../utilities/translate/translate.service';

/**
 * Log core service.
 *
 * @todo Should this service be deprecated in favor of integration
 *  with the ElementService?
 */
@Injectable({
  providedIn: 'root'
})
export class LogCoreService {

  /** Changes subject. */
  protected _changes: Subject<any[]> = new BehaviorSubject<any[]>(null);

  /** Changes observable. */
  public change$ = this._changes.asObservable();

  /** Comments subject. */
  protected _comments: Subject<any[]> = new BehaviorSubject<any[]>(null);

  /** Comments observable. */
  public comment$ = this._comments.asObservable();

  /** Counts subject. */
  protected counts: Subject<ElementCommentSummary[]> = new BehaviorSubject<ElementCommentSummary[]>(null);

  /** Counts observable. */
  public count$ = this.counts.asObservable();

  constructor(
    protected apiService: ApiService,
    protected constantsService: ConstantsService,
    protected elementService: ElementService,
    protected moduleService: ModuleService,
    protected submoduleService: SubmoduleService,
    protected userService: UserService,
    protected translateService: TranslateService
  ) { }

  /**
   * Comment count resolver.
   *
   * @note This method is currently copy-pasted to the element service
   *  to prevent circular dependencies. When migrating to Angular Router
   *  the results should be saved in a Router Snapshot, which can be read
   *  by the element service *without* circular errors.
   *
   * @param [projectId] Optional projectId when using in a resolver.
   * @returns Observable with comment count summaries.
   */
  public resolveCommentCounts(projectId: Project['_id']): Observable<ElementCommentSummary[]> {
    return new Observable(observer => {
      this.apiService.getElementCommentCount().subscribe(counts => {
        this._comments.next(counts.summary);
        observer.next(counts.summary);
        observer.complete();
      });
    });
  }

  /**
   * Add extra properties to changes.
   *
   * @returns Enriched changes array.
   */
  protected _enrichChanges(changes: ElementChange[]): ElementChange[] {
    for (const change of changes) {
      change.elementName = this.elementService.getName(change.elementId, { constructionId: change.constructionId });
      change.moduleName = this.moduleService.getName(change.moduleId);
      change.text = this._getChangeText(change);
    }
    return changes;
  }

  /**
   * Add extra properties to comments.
   *
   * @returns Enriched comments array.
   */
  protected _enrichComments(comments: ElementComment[]): ElementComment[] {
    for (const comment of comments) {
      comment.elementName = this.elementService.getName(comment.elementId, { constructionId: comment.constructionId });
      comment.moduleName = this.moduleService.getName(comment.moduleId);
    }
    return comments;
  }

  /**
   * Filter inactive log items.
   *
   * All changes and comments without an active parent element are removed from the list.
   *
   * @note This will be deprecated once solved back-end.
   * @todo Remove after solving back-end.
   *
   * @param logs Element logs object.
   * @returns Filtered logs.
   */
  protected _filterInactive(logs: ElementLogs): ElementLogs {

    const changes: ElementChange[] = [];
    for (const change of logs.changes) {
      const element = this.elementService.getElement(change.elementId, { constructionId: change.constructionId });
      if (element !== undefined) {
        changes.push(change);
      }
    }
    for (const comment of logs.comments) {
      const element = this.elementService.getElement(comment.elementId, { constructionId: comment.constructionId });
      comment.active = comment.active === true && element !== undefined;
    }
    logs.comments = logs.comments.filter(c => c.active);

    return {
      changes: changes,
      comments: logs.comments
    };
  }

  /**
   * Get text for a change.
   *
   * @todo Translations.
   *
   * @param change The change to get the text for.
   * @returns The generated text for the change.
   */
  protected _getChangeText(change: ElementChange): ElementChange['text'] {

    /**
     * @note Sometimes the element can not be found, why?
     * @todo Remove block after fix.
     */
    const el = this.elementService.getElement(change.elementId, { constructionId: change.constructionId });
    if (!el) {
      console.log('change', change);
      console.log('element:', el);
    }

    const element = this.elementService.getElement(change.elementId, { constructionId: change.constructionId });
    switch (change.action) {
      case 'copied.from.template':
        return `<strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('iscreatedcopiedfromtemplate')
        }`;
      case 'copied.from.project':
        return `<strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('iscreatedcopiedfromproject')
        }`;
      case 'created':
        return `<strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('iscreated')
        }`;
      case 'edited.assignedToUserId':
        return `<strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('isassigned')
        }`;
      case 'edited.name':
        return `${this.translateService.get('nameofspecification')} <strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('ischanged')
        }`;
      case 'edited.status':
        return `${this.translateService.get('statusof')} <strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('ischanged')
        }`;
      case 'edited.value':
        return `${this.translateService.get('valueof')}  <strong>${ change.elementName } (${ element.code })</strong> ${
          this.translateService.getLowerCase('ischanged')
        }`;
    }
  }
}
